import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, combineLatest, skip, switchMap, take } from 'rxjs';
import { Socket, io } from 'socket.io-client';
import { environment } from '../../environments/environment';
import { ISentNotification } from '../interfaces/';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private refreshNotification = new BehaviorSubject<number | null>(null);
  private forwardMessage = new BehaviorSubject<string>('');
  forwardMessage$ = this.forwardMessage.asObservable().pipe(skip(1));
  refreshNotification$ = combineLatest([
    this.refreshNotification,
    this.authService.authBufferSub$
  ]).pipe(
    switchMap(([strategyId, user]) => {
      const hasToken = !!this.authService.getAccessToken() && !!user;
      if (!hasToken || !strategyId) {
        return EMPTY;
      }
      return this.getNotifications(strategyId);
    })
  );

  private socket?: Socket;
  private domain = environment.domain;

  constructor(private authService: AuthService, private http: HttpClient) {
    this.authService.authBufferSub$.subscribe((user) => {
      const token = this.authService.getAccessToken();
      if (!token || !user) {
        this.disconnect();
        return;
      }

      if (this.socket) {
        this.socket.auth = { token };
        return;
      }

      this.socket = io(environment.base_url, {
        auth: { token },
        autoConnect: false,
        secure: environment.production
      });
    });
  }

  disconnect() {
    if (this.socket?.connected) {
      this.socket.disconnect();
    }
  }

  connect() {
    if (!this.socket) {
      return;
    }
    if (this.socket.connected) {
      return;
    }
    this.socket.connect();
    this.socket.on('connection', () => {
      // connected
    });
  }

  sendMessage(message: Partial<ISentNotification> | string, isServerSide = false) {
    if (!this.socket) {
      return;
    }
    if (!isServerSide) {
      this.socket.emit('message', message, isServerSide);
    }
  }

  joinProject(projectId: number) {
    this.socket?.emit('joinProject', projectId);
  }

  leaveProject(projectId: number) {
    this.socket?.emit('leaveProject', projectId);
  }

  sendMessageToProject(message: unknown) {
    this.socket?.emit('sendMessageToProject', message);
  }

  listen(event: string) {
    return new Observable<unknown>(observer => {
      if (!this.socket) {
        observer.complete();
        return;
      }
      this.socket.on(event, (message, isServerSide) => {
        if (typeof message === 'object' || !isServerSide) {
          observer.next(message);
        }
      });
    });
  }

  getMessages() {
    return new Observable<unknown>(observer => {
      if (!this.socket) {
        observer.complete();
        return;
      }
      this.socket.on('message', (data) => {
        observer.next(data);
      });
      return () => {
        this.disconnect();
      };
    });
  }

  listenToProjectMessages() {
    return new Observable<unknown>(observer => {
      if (!this.socket) {
        observer.complete();
        return;
      }
      this.socket.on('receiveProjectMessage', (data) => {
        observer.next(data);
      });
    });
  }

  getNotifications(strategyId: number) {
    return this.http.get(`${this.domain}notifications?strategyId=${strategyId}`).pipe(take(1));
  }

  setRefreshNotification(strategyId: number) {
    this.refreshNotification.next(strategyId);
  }

  forwardMessageFn(message: unknown) {
    this.forwardMessage.next(typeof message === 'string' ? message : JSON.stringify(message));
  }
}
